import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import moment from "moment";
import Select from "react-select";
import PhoneInput from "react-phone-number-input";
import iso from "iso-3166-1";
import DatePicker from "react-datepicker";
import "react-phone-number-input/style.css";
import "react-datepicker/dist/react-datepicker.css";

import Loader from "../../reusables/Loader";
import Swal from "sweetalert2";
import * as api from "../../../api";
import {
  bookingServiceOptions,
  regionOptions,
  idTypeOptions,
  yearsExperienceOptions,
  dressSizeOptions,
  documentUploadOptions,
} from "../../../utils/signupPartnerOptions";

const SignupPartner = () => {
  const serverEnv = process.env.REACT_APP_SERVER_ENV || "uk";
  const [loading, setLoading] = useState(false);
  const [errorIndicator, setErrorIndicator] = useState();
  const [countries, setCountries] = useState([]);
  const [signupError, setSignupError] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [practiceOptions, setPracticeOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [signupForm, setSignupForm] = useState({
    // first page
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    dateOfBirth: "",
    address1: "",
    address2: "",
    city: "",
    countryCode: "GBR",
    zipCode: "",
    contactNumber: "",
    residentialStatus: "",
    hasValidDriversLicense: false,
    hasOwnVehicle: false,
    hasRightToWork: false,
    practices: [], // discipline
    bookingService: "",
    region: "",
    idType: "",
    preferredWorkLocation: "",
    // second page
    has30podiatryEquipment: false,
    yearsExperience: "",
    potentialStartDate: "",
    dressSize: "",
    documentUpload: "",
    hasConsented: false,
    workDayAvailability: {
      Monday: "08:00 AM - 05:00 PM",
      Tuesday: "08:00 AM - 05:00 PM",
      Wednesday: "08:00 AM - 05:00 PM",
      Thursday: "08:00 AM - 05:00 PM",
      Friday: "08:00 AM - 05:00 PM",
    },
    identification: [],
    certificate: [],
    clientApp: ["HAPIPARTNERS"],
    facilityIds: [],
  });

  const [mobileNumber, setMobileNumber] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState([]);
  const [selectedBookingService, setSelectedBookingService] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [selectedIdType, setSelectedIdType] = useState([]);
  const [selectedWorkLocation, setSelectedWorkLocation] = useState([]);
  const [selectedYearsExperience, setSelectedYearsExperience] = useState([]);
  const [selectedDressSize, setSelectedDressSize] = useState([]);
  const [selectedDocumetUpload, setSelectedDocumetUpload] = useState([]);

  const regionMapping = {
    aus: ["Sydney"],
    nz: ["Auckland"],
    uk: regionOptions.filter((r) => r !== "Sydney" && r !== "Auckland"),
  };

  const getRegionOptions = (() => {
    if (!serverEnv) return [];

    const regions = regionMapping[serverEnv] || [];

    return regions.map((region) => ({
      value: region.split("(")[0].trim(),
      label: region,
    }));
  })();

  useEffect(() => {
    const allowedAlpha3 = ["GBR", "AUS", "NZL"];
    const allCountries = iso.all?.() || [];
    if (allCountries.length > 0) {
      setCountries(
        allCountries.filter((c) => allowedAlpha3.includes(c.alpha3))
      );
    }
  }, []);

  useEffect(() => {
    const getPractice = async () => {
      setLoading(true);
      try {
        let practicesArray = [];
        const data = await api.getPractices();
        const practice = data.data.data;
        practice.forEach((item) => {
          practicesArray.push({
            value: item.name,
            label: item.label,
          });
        });
        setPracticeOptions(practicesArray);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getPractice();
  }, []);

  const [availability, setAvailability] = useState({
    monday: { start: "08:00", end: "17:00" },
    tuesday: { start: "08:00", end: "17:00" },
    wednesday: { start: "08:00", end: "17:00" },
    thursday: { start: "08:00", end: "17:00" },
    friday: { start: "08:00", end: "17:00" },
  });

  const handleAvailabilityChange = (day, field, value) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const formatTime = (time) => {
    if (!time) return "";
    return moment(time, "HH:mm").format("hh:mm A");
  };

  useEffect(() => {
    const formatted = {};
    Object.entries(availability).forEach(([day, { start, end }]) => {
      const Day = day.charAt(0).toUpperCase() + day.slice(1);
      if (start && end) {
        formatted[Day] = `${formatTime(start)} - ${formatTime(end)}`;
      } else {
        formatted[Day] = "";
      }
    });
    setSignupForm((prev) => ({
      ...prev,
      workDayAvailability: formatted,
    }));
  }, [availability]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      contactNumber: mobileNumber,
    }));
  }, [mobileNumber]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      practices: selectedDiscipline.map((item) => item.value),
    }));
  }, [selectedDiscipline]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      bookingService: selectedBookingService?.value || "",
    }));
  }, [selectedBookingService]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      region: selectedRegion?.value || "",
    }));
  }, [selectedRegion]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      idType: selectedIdType?.value || "",
    }));
  }, [selectedIdType]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      preferredWorkLocation: selectedWorkLocation?.value || "",
    }));
  }, [selectedWorkLocation]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      yearsExperience: selectedYearsExperience?.value || "",
    }));
  }, [selectedYearsExperience]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      dressSize: selectedDressSize?.value || "",
    }));
  }, [selectedDressSize]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      documentUpload: selectedDocumetUpload?.value || "",
    }));
  }, [selectedDocumetUpload]);

  useEffect(() => {
    if (!signupForm.practices.includes("podiatrist")) {
      setSignupForm((prev) => ({
        ...prev,
        has30podiatryEquipment: false,
      }));
    }
  }, [signupForm.practices]);

  useEffect(() => {
    if (!signupForm.preferredWorkLocation !== "UK Region") {
      setSignupForm((prev) => ({
        ...prev,
        region: "",
      }));
      setSelectedRegion([]);
    }
  }, [signupForm.preferredWorkLocation]);

  useEffect(() => {
    if (
      (confirmPassword && signupForm.password !== confirmPassword) ||
      (signupForm.password && signupForm.password.length < 8)
    ) {
      setErrorIndicator("password");
    } else if (errorIndicator === "password") {
      setErrorIndicator(undefined);
    }
  }, [signupForm.password, confirmPassword]);

  useEffect(() => {
    if (signupError) {
      const errors = signupError.map((err) => err.message).join("<br>");
      Swal.fire({
        title: "Error!",
        html: errors,
        icon: "error",
        confirmButtonColor: "#325ba6",
      });
    }
  }, [signupError]);

  const handleChange = (event) => {
    const { value, name, type, checked } = event.target;
    if (type === "checkbox") {
      setSignupForm({ ...signupForm, [name]: checked });
    } else {
      setSignupForm({ ...signupForm, [name]: value });
    }
  };

  const handleSelectOption = (selected, name) => {
    if (name === "discipline") {
      setSelectedDiscipline(selected);
    } else if (name === "bookingService") {
      setSelectedBookingService(selected);
    } else if (name === "region") {
      setSelectedRegion(selected);
    } else if (name === "preferredWorkLocation") {
      setSelectedWorkLocation(selected);
    } else if (name === "yearsExperience") {
      setSelectedYearsExperience(selected);
    } else if (name === "dressSize") {
      setSelectedDressSize(selected);
    } else if (name === "documentUpload") {
      setSelectedDocumetUpload(selected);
    } else {
      setSelectedIdType(selected);
    }
  };
  const imageHandleChange = (e, name) => {
    const tempFiles = Array.from(e.target.files).map((file) => file);
    if (tempFiles.filter((file) => file.size > 1000000).length > 0) {
      Swal.fire({
        html: "Can only upload files less than 1MB",
        icon: "error",
        title: "Error",
        confirmButtonColor: "#325ba6",
      });
      return;
    }
    setSignupForm({
      ...signupForm,
      [name]: tempFiles.filter((file) => file.size < 1000000),
    });
  };

  const handleChangeDate = (date, name) => {
    setSignupForm({
      ...signupForm,
      [name]: date ? moment(date).format("YYYY-MM-DD") : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();

      // Append all fields except files
      Object.entries(signupForm).forEach(([key, value]) => {
        if (
          key === "certificate" ||
          key === "identification" ||
          key === "clientApp"
        )
          return;

        if (Array.isArray(value) || typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      formData.append("clientApp[]", "HAPIPARTNERS");

      // Append certificate file(s)
      if (signupForm.certificate && signupForm.certificate.length) {
        signupForm.certificate.forEach((file) => {
          formData.append("certificate", file);
        });
      }

      // Append identification file(s)
      if (signupForm.identification && signupForm.identification.length) {
        signupForm.identification.forEach((file) => {
          formData.append("identification", file);
        });
      }

      const data = await api.signUpPractitioner(formData);
      Swal.fire({
        html: "Successfully signed up! An email will be sent once the admin approved your request!",
        icon: "success",
        title: "Success",
        confirmButtonColor: "#325ba6",
      });
      setSignupForm({
        // first page
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        dateOfBirth: "",
        address1: "",
        address2: "",
        city: "",
        countryCode: "GBR",
        zipCode: "",
        contactNumber: "",
        residentialStatus: "",
        hasValidDriversLicense: false,
        hasOwnVehicle: false,
        hasRightToWork: false,
        practices: [], // discipline
        bookingService: "",
        region: "",
        idType: "",
        preferredWorkLocation: "",
        // second page
        has30podiatryEquipment: false,
        yearsExperience: "",
        potentialStartDate: "",
        dressSize: "",
        documentUpload: "",
        hasConsented: false,
        workDayAvailability: {
          Monday: "08:00 AM - 05:00 PM",
          Tuesday: "08:00 AM - 05:00 PM",
          Wednesday: "08:00 AM - 05:00 PM",
          Thursday: "08:00 AM - 05:00 PM",
          Friday: "08:00 AM - 05:00 PM",
        },
        identification: [],
        certificate: [],
        clientApp: ["HAPIPARTNERS"],
        facilityIds: [],
      });
      setMobileNumber("");
      setConfirmPassword("");
      setSelectedDiscipline([]);
      setSelectedBookingService([]);
      setSelectedRegion([]);
      setSelectedIdType([]);
      setSelectedWorkLocation([]);
      setSelectedYearsExperience([]);
      setSelectedDressSize([]);
      setSelectedDocumetUpload([]);
    } catch (error) {
      setSignupError(error.response.data.errors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <div className="h-100 d-flex">
          <Loader />
        </div>
      ) : (
        <div className="container mt-3" style={{ margin: "150px auto" }}>
          <div className="container p-3 mt-2 bg-light border shadow-sm">
            <form>
              <div className="form-group">
                <p
                  className="text-center lead mb-4"
                  style={{ fontSize: "25px" }}
                >
                  Sign Up Partner
                </p>

                {page === 1 && (
                  <>
                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="firstName">First Name</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "firstName"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="firstName"
                          value={signupForm.firstName.toLowerCase() ?? ""}
                          autoComplete="off"
                        />
                      </div>
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="lastName">Last Name</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "lastName"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="lastName"
                          value={signupForm.lastName.toLowerCase() ?? ""}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="email">Email</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "email"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="email"
                          name="email"
                          value={signupForm.email.toLowerCase() ?? ""}
                          autoComplete="off"
                        />
                      </div>
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="password">Password</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "password"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="password"
                          name="password"
                          value={signupForm.password ?? ""}
                          autoComplete="off"
                        />
                        {errorIndicator === "password" && (
                          <span
                            className="text-danger"
                            style={{ fontSize: "0.9em" }}
                          >
                            {signupForm.password.length < 8
                              ? "Password must be at least 8 characters."
                              : signupForm.password !== confirmPassword
                              ? "Passwords do not match."
                              : ""}
                          </span>
                        )}
                      </div>
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="confirmPassword">
                          Confirm Password
                        </label>
                        <input
                          className={`form-control ${
                            errorIndicator === "password"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="password"
                          name="confirmPassword"
                          value={confirmPassword ?? ""}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="contactNumber">Contact Number</label>
                        <PhoneInput
                          className={`form-control phone-input ${
                            errorIndicator === "contactNumber"
                              ? "border border-danger"
                              : ""
                          }`}
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="GB"
                          value={mobileNumber}
                          onChange={setMobileNumber}
                        />
                      </div>
                      <div className="col-sm-12 col-md-6 d-flex flex-column">
                        <label htmlFor="dateOfBirth">Date Of Birth</label>
                        <DatePicker
                          selected={signupForm.dateOfBirth}
                          onChange={(e) => handleChangeDate(e, "dateOfBirth")}
                          dateFormat="dd/MM/yyyy"
                          className={`form-control ${
                            errorIndicator === "potentialStartDate"
                              ? "border border-danger"
                              : ""
                          }`}
                          dateFormatCalendar="MMMM"
                          showMonthDropdown
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={100}
                          maxDate={new Date()}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="address1">Address Line 1</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "address1"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="address1"
                          value={signupForm.address1 ?? ""}
                          autoComplete="off"
                        />
                      </div>
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="address2">Address Line 2</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "address2"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="address2"
                          value={signupForm.address2 ?? ""}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="city">City</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "city"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="city"
                          value={signupForm.city ?? ""}
                          autoComplete="off"
                        />
                      </div>

                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="countryCode">Country</label>
                        <select
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "countryCode"
                              ? "border border-danger"
                              : ""
                          }`}
                          id="country"
                          name="countryCode"
                          value={signupForm.countryCode ?? "GBR"}
                        >
                          <option disabled>SELECT COUNTRY</option>
                          {countries &&
                            countries.map((data, index) => (
                              <option value={data.alpha3} key={index}>
                                {data.country}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="zipCode">Zip Code</label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "zipCode"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="zipCode"
                          value={signupForm.zipCode ?? ""}
                          autoComplete="off"
                        />
                      </div>
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="residentialStatus">
                          Residential Status{" "}
                          <i
                            className="fa fa-question-circle text-muted"
                            data-toggle="tooltip"
                            title="Homeowner, Tenant, Living with Relatives"
                            style={{ cursor: "pointer", fontSize: "12px" }}
                          ></i>
                        </label>
                        <input
                          onChange={handleChange}
                          className={`form-control ${
                            errorIndicator === "residentialStatus"
                              ? "border border-danger"
                              : ""
                          }`}
                          type="text"
                          name="residentialStatus"
                          value={signupForm.residentialStatus ?? ""}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="discipline">Discipline</label>
                        <Select
                          className={
                            errorIndicator === "discipline"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select Discipline"
                          isClearable
                          isMulti
                          options={practiceOptions}
                          value={selectedDiscipline}
                          onChange={(e) => handleSelectOption(e, "discipline")}
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="bookingService">Booking Service</label>
                        <Select
                          className={
                            errorIndicator === "bookingService"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select Booking Service"
                          isClearable
                          options={bookingServiceOptions.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          value={selectedBookingService}
                          onChange={(e) =>
                            handleSelectOption(e, "bookingService")
                          }
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="preferredWorkLocation">
                          Preferred Work Location
                        </label>
                        <Select
                          className={
                            errorIndicator === "preferredWorkLocation"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select Booking Service"
                          isClearable
                          options={[
                            // {
                            //   value: "North London, UK",
                            //   label: "North London, UK",
                            // },
                            // {
                            //   value: "South London, UK",
                            //   label: "South London, UK",
                            // },
                            // {
                            //   value: "Northern Ireland, UK",
                            //   label: "Northern Ireland, UK",
                            // },
                            {
                              value: "UK Region",
                              label: "UK Region",
                            },
                            {
                              value: "Sydney, Australia",
                              label: "Sydney, Australia",
                            },
                            {
                              value: "Auckland, New Zealand",
                              label: "Auckland, New Zealand",
                            },
                          ]}
                          value={selectedWorkLocation}
                          onChange={(e) =>
                            handleSelectOption(e, "preferredWorkLocation")
                          }
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-6">
                        <label htmlFor="idType">ID Type</label>
                        <Select
                          className={
                            errorIndicator === "idType"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select ID Type"
                          isClearable
                          options={idTypeOptions.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          value={selectedIdType}
                          onChange={(e) => handleSelectOption(e, "idType")}
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>

                      {signupForm.preferredWorkLocation === "UK Region" && (
                        <div className="col-sm-12 col-md-6">
                          <label htmlFor="region">Region</label>
                          <Select
                            className={
                              errorIndicator === "region"
                                ? "border border-danger"
                                : ""
                            }
                            placeholder="Select Region"
                            isClearable
                            options={getRegionOptions}
                            value={selectedRegion}
                            onChange={(e) => handleSelectOption(e, "region")}
                            inputProps={{ autoComplete: "off" }}
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="row">
                        <div className="col-sm-12 col-md-12">
                          <div className="d-flex mt-3 align-items-center">
                            <input
                              type="checkbox"
                              name="hasRightToWork"
                              checked={signupForm.hasRightToWork}
                              onChange={handleChange}
                              style={{
                                width: "20px",
                                height: "20px",
                                accentColor: "#325ba6",
                              }}
                            />
                            <label
                              htmlFor="hasRightToWork"
                              className="ml-2 my-auto"
                            >
                              Do you have the right to work in the country for
                              which this application is being made?
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-12 col-md-12">
                          <div className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              name="hasValidDriversLicense"
                              checked={signupForm.hasValidDriversLicense}
                              onChange={handleChange}
                              style={{
                                width: "20px",
                                height: "20px",
                                accentColor: "#325ba6",
                              }}
                            />
                            <label
                              htmlFor="hasValidDriversLicense"
                              className="ml-2 my-auto"
                            >
                              Do you have a Valid Driver's License for the
                              country for which this application is being made?
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-12 col-md-12">
                          <div className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              name="hasOwnVehicle"
                              checked={signupForm.hasOwnVehicle}
                              onChange={handleChange}
                              style={{
                                width: "20px",
                                height: "20px",
                                accentColor: "#325ba6",
                              }}
                            />
                            <label
                              htmlFor="hasOwnVehicle"
                              className="ml-2 my-auto"
                            >
                              Do you have access to your own vehicle?
                            </label>
                          </div>
                        </div>
                      </div>

                      {signupForm.practices.includes("podiatrist") && (
                        <>
                          <div className="row">
                            <div className="col-sm-12 col-md-12">
                              <div className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  name="has30podiatryEquipment"
                                  checked={signupForm.has30podiatryEquipment}
                                  onChange={handleChange}
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    accentColor: "#325ba6",
                                  }}
                                />
                                <label
                                  htmlFor="has30podiatryEquipment"
                                  className="ml-2 my-auto"
                                >
                                  For Podiatry: Do you have at least 30 sets of
                                  your own equipment?
                                </label>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {page === 2 && (
                  <>
                    <div className="row">
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="yearsExperience">
                          Number Of Years Experience
                        </label>
                        <Select
                          className={
                            errorIndicator === "yearsExperience"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select Years Experience"
                          isClearable
                          options={yearsExperienceOptions.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          value={selectedYearsExperience}
                          onChange={(e) =>
                            handleSelectOption(e, "yearsExperience")
                          }
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>

                      <div className="col-sm-12 col-md-4 d-flex flex-column">
                        <label htmlFor="potentialStartDate">
                          Potential Start Date
                        </label>
                        <DatePicker
                          id="potentialStartDate"
                          selected={signupForm.potentialStartDate}
                          onChange={(e) =>
                            handleChangeDate(e, "potentialStartDate")
                          }
                          dateFormat="dd/MM/yyyy"
                          minDate={moment().add(14, "days").toDate()}
                          className={`form-control ${
                            errorIndicator === "potentialStartDate"
                              ? "border border-danger"
                              : ""
                          }`}
                          placeholderText="Select Start Date"
                          autoComplete="off"
                        />
                      </div>

                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="dressSize">
                          Dress Size (For Scrubs)
                        </label>
                        <Select
                          className={
                            errorIndicator === "dressSize"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select Dress Size"
                          isClearable
                          options={dressSizeOptions.map((item) => ({
                            value: item,
                            label: item,
                          }))}
                          value={selectedDressSize}
                          onChange={(e) => handleSelectOption(e, "dressSize")}
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-4">
                        <label htmlFor="documentUpload">Document Upload</label>
                        <Select
                          className={
                            errorIndicator === "documentUpload"
                              ? "border border-danger"
                              : ""
                          }
                          placeholder="Select Document Upload"
                          isClearable
                          options={documentUploadOptions}
                          value={selectedDocumetUpload}
                          onChange={(e) =>
                            handleSelectOption(e, "documentUpload")
                          }
                          inputProps={{ autoComplete: "off" }}
                        />
                      </div>
                    </div>

                    <div className="mt-1">
                      <label>Certificate and Identification</label>
                    </div>

                    <div className="d-md-flex mt-md-2">
                      <div className="col-sm-12 col-md-6">
                        <label>Certificate</label>
                        <div className="row">
                          <div className="col-12">
                            <input
                              type="file"
                              name="certificate"
                              onChange={(e) =>
                                imageHandleChange(e, "certificate")
                              }
                              style={{ cursor: "pointer" }}
                              // multiple
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-12 col-md-6">
                        <label>ID</label>
                        <div className="row">
                          <div className="col-12">
                            <input
                              type="file"
                              name="identification"
                              onChange={(e) =>
                                imageHandleChange(e, "identification")
                              }
                              style={{ cursor: "pointer" }}
                              // multiple
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2">
                      <label>Work Day and Time Availability</label>
                    </div>

                    <div className="row">
                      {[
                        "monday",
                        "tuesday",
                        "wednesday",
                        "thursday",
                        "friday",
                      ].map((day) => (
                        <div className="col-12 mb-2" key={day}>
                          <div className="d-flex align-items-center">
                            <label
                              className="me-2 text-capitalize mr-5"
                              style={{ width: 80 }}
                            >
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </label>
                            <input
                              type="time"
                              className="form-control me-2"
                              value={availability[day].start}
                              onChange={(e) =>
                                handleAvailabilityChange(
                                  day,
                                  "start",
                                  e.target.value
                                )
                              }
                              style={{ maxWidth: 130 }}
                            />
                            <span className="mx-1"> - </span>
                            <input
                              type="time"
                              className="form-control"
                              value={availability[day].end}
                              onChange={(e) =>
                                handleAvailabilityChange(
                                  day,
                                  "end",
                                  e.target.value
                                )
                              }
                              style={{ maxWidth: 130 }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="row">
                      <div className="col-sm-12 col-md-12">
                        <div className="d-flex mt-1 mb-2 align-items-center">
                          <input
                            type="checkbox"
                            name="hasConsented"
                            checked={signupForm.hasConsented}
                            onChange={handleChange}
                            style={{
                              width: "20px",
                              height: "20px",
                              accentColor: "#325ba6",
                            }}
                          />
                          <label
                            htmlFor="hasConsented"
                            className="ml-2 my-auto"
                          >
                            By submitting this application, I confirm that I
                            consent to the collection, storage, and verification
                            of the following documents as part of the
                            registration and compliance process. I understand
                            that these documents will be handled in accordance
                            with applicable data protection laws and used solely
                            for the purposes of verifying my eligibility,
                            credentials, and suitability for engagement.
                          </label>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="d-flex justify-content-center mt-4">
                  <span
                    onClick={() => setPage(1)}
                    style={{
                      width: page === 1 ? "14px" : "12px",
                      height: page === 1 ? "14px" : "12px",
                      margin: "0 5px",
                      borderRadius: "50%",
                      backgroundColor: page === 1 ? "#325ba6" : "#ccc",
                      cursor: "pointer",
                      display: "inline-block",
                      transition: "all 0.2s ease",
                    }}
                  ></span>
                  <span
                    onClick={() => setPage(2)}
                    style={{
                      width: page === 2 ? "14px" : "12px",
                      height: page === 2 ? "14px" : "12px",
                      margin: "0 5px",
                      borderRadius: "50%",
                      backgroundColor: page === 2 ? "#325ba6" : "#ccc",
                      cursor: "pointer",
                      display: "inline-block",
                      transition: "all 0.2s ease",
                    }}
                  ></span>
                </div>

                <button
                  className="btn btn-primary mt-3 w-100 py-3"
                  onClick={handleSubmit}
                  disabled={
                    errorIndicator === "password" ||
                    signupForm.password.length < 8 ||
                    signupForm.password !== confirmPassword ||
                    !signupForm.hasConsented
                  }
                >
                  Sign Up
                </button>

                <NavLink className="text-center d-block mt-2" to="/login">
                  Go back to login
                </NavLink>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SignupPartner;
